// Plays muted background videos only while they are near the viewport. An autoplay
// attribute makes the browser fetch the whole clip at page load, even far below the fold.
// The element itself is display: contents, so each <video> inside is observed directly;
// a video hidden for the current breakpoint never intersects and never loads.
// A video with data-poster gets its poster only when it comes near, too.
if (!customElements.get('inview-video')) {
  class InviewVideo extends HTMLElement {
    constructor() {
      super();
      this.onPlay = this.handlePlay.bind(this);
      this.onPause = this.handlePause.bind(this);
      this.inView = new Set();
      // Pauses we triggered ourselves, so they are not mistaken for the visitor's.
      this.autoPaused = new WeakSet();
      this.userPaused = new WeakSet();
    }

    connectedCallback() {
      this.videos = Array.from(this.querySelectorAll('video'));
      if (this.videos.length === 0) return;
      // Visitors who asked for less motion get the poster and can start the clip themselves.
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!('IntersectionObserver' in window)) {
        this.videos.forEach((video) => {
          this.setPoster(video);
          if (!this.reducedMotion) this.play(video);
        });
        return;
      }

      // Media events don't bubble, but they do pass ancestors in the capture phase.
      this.addEventListener('play', this.onPlay, true);
      this.addEventListener('pause', this.onPause, true);
      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), { rootMargin: '200px 0px' });
      this.videos.forEach((video) => this.observer.observe(video));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('play', this.onPlay, true);
      this.removeEventListener('pause', this.onPause, true);
      this.inView.clear();
    }

    handleIntersect(entries) {
      entries.forEach(({ target: video, isIntersecting }) => {
        if (isIntersecting) {
          this.setPoster(video);
          if (this.reducedMotion) return;
          this.inView.add(video);
          if (video.paused && !this.userPaused.has(video)) this.play(video);
        } else {
          this.inView.delete(video);
          if (!video.paused) {
            this.autoPaused.add(video);
            video.pause();
          }
        }
      });
    }

    setPoster(video) {
      if (!video.dataset.poster) return;
      video.poster = video.dataset.poster;
      delete video.dataset.poster;
    }

    play(video) {
      if (video.ended && !video.loop) return;
      video.play().catch(() => {});
    }

    handlePlay(event) {
      this.userPaused.delete(event.target);
    }

    handlePause(event) {
      const video = event.target;
      if (this.autoPaused.has(video)) {
        this.autoPaused.delete(video);
        return;
      }
      // A pause on a clip that is on screen came from the visitor (controls or a play
      // toggle); it stays paused until they start it again. getClientRects() rules out
      // a clip another script just hid for the other breakpoint.
      if (
        this.inView.has(video) &&
        !video.ended &&
        document.visibilityState === 'visible' &&
        video.getClientRects().length > 0
      ) {
        this.userPaused.add(video);
      }
    }
  }

  customElements.define('inview-video', InviewVideo);
}
